import React from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import { ContainerProvider } from "@linkfluence/core/di";
import Loading from "@ui-kit/splash-screen";
import Main from "@radarly/app/pages/main";
import RadarlyApp from "@radarly/core/app";
import Bootstrap from "@radarly/core/bootstrap";
import { getContainer } from "@radarly/core/container";
import Suspense from "@radarly/shared/bootstrap/suspense";
import { AppContext } from "@radarly/shared/context";
import { IRouterService } from "@radarly/shared/router";
import { Page } from "@radarly/shared/router/pages";
import { CustomRouterService } from "./router";

const container = getContainer();

const router = new CustomRouterService();

const Home = React.lazy(() => import("../../app/src/pages/home"));
const ProjectHome = React.lazy(
  () => import("../../app/src/pages/project/home")
);
const InsightPagesHome = React.lazy(
  () => import("../../app/src/pages/insight-pages/home")
);
const InsightPage = React.lazy(
  () => import("../../app/src/pages/insight-pages/page")
);
const Editor = React.lazy(
  () => import("../../app/src/pages/insight-pages/editor")
);
const WizardEditor = React.lazy(
  () => import("../../app/src/pages/insight-pages/wizard-editor")
);
const Discovery = React.lazy(
  () => import("../../app/src/pages/insight-pages/discovery")
);
const ImageClustering = React.lazy(
  () => import("../../app/src/pages/image-clustering")
);
const EmptyHome = React.lazy(() => import("../../app/src/pages/empty"));
const Restricted = React.lazy(() => import("../../app/src/pages/restricted"));
const SharedPage = React.lazy(() => import("@radarly/shared-pages/app"));
const ConnectedRoute = ({
  component: Component,
  path,
  exact,
}: {
  path: string;
  exact?: boolean;
  component: React.FC<{ router: IRouterService }>;
}) => {
  return (
    <Route
      path={path}
      exact={exact}
      render={() => (
        <Bootstrap
          onAccessError={() => (
            <Main router={router}>
              <Restricted />
            </Main>
          )}
          onLoading={() => <Loading />}
        >
          <Main router={router}>
            <Component router={router} />
          </Main>
        </Bootstrap>
      )}
    />
  );
};

export default () => {
  return (
    <ContainerProvider container={container}>
      <RadarlyApp>
        <AppContext>
          <Suspense>
            <Switch>
              <Route path="/no_project" component={EmptyHome} />
              <ConnectedRoute
                exact
                path={router.getPathFromPage(Page.home)}
                component={Home}
              />
              <ConnectedRoute
                path={router.getPathFromPage(Page.projectHome)}
                component={ProjectHome}
              />
              <Redirect
                from={router.getPathFromPage(Page.insightPagesList)}
                to={router.getPathFromPage(Page.insightPagesHome)}
              />
              <ConnectedRoute
                path={router.getPathFromPage(Page.insightPagesHome)}
                component={InsightPagesHome}
              />
              <ConnectedRoute
                path={router.getPathFromPage(Page.insightPagesPage)}
                component={InsightPage}
              />
              <ConnectedRoute
                path={router.getPathFromPage(Page.insightPagesGlobalPage)}
                component={InsightPage}
              />
              <ConnectedRoute
                path={router.getPathFromPage(Page.insightPagesEditor)}
                component={Editor}
              />
              <ConnectedRoute
                path={router.getPathFromPage(Page.insightPagesGlobalEditor)}
                component={Editor}
              />
              <ConnectedRoute
                path={router.getPathFromPage(Page.insightPagesWizardEditor)}
                component={WizardEditor}
              />
              <ConnectedRoute
                path={router.getPathFromPage(Page.discovery)}
                component={Discovery}
              />
              <ConnectedRoute
                path={router.getPathFromPage(Page.imageClustering)}
                component={ImageClustering}
              />
              <ConnectedRoute
                path={router.getPathFromPage(Page.sharedInsightPage)}
                component={SharedPage}
              />
              <Route path="*">
                <Redirect to="/" />
              </Route>
            </Switch>
          </Suspense>
        </AppContext>
      </RadarlyApp>
    </ContainerProvider>
  );
};
